/**
 * Contrato canonico v1.0.0 do payload `metadata` enviado em
 * ProjectCommit / ProjectDraft.
 * Mantido client-side; backend so valida shape de dict <= 2MB.
 *
 * Fonte: integration-plan/context/metadata-contract.md
 */

import type {
  AnchorSide,
  ColumnKey,
  DatabaseDialect,
  LogicalType,
  RelationType,
} from '@/types'

export const METADATA_SCHEMA_VERSION = '1.1.0' as const

export type SchemaVersion = `${number}.${number}.${number}`

export interface MetadataAnchor {
  side: AnchorSide
  offsetPercent: number
}

export interface MetadataColumn {
  /** UUID estável da coluna (identidade; presente a partir do schema 1.1.0). */
  id: string
  key: ColumnKey
  pk?: boolean
  fk?: boolean
  uq?: boolean
  name: string
  type: string
  logicalType?: LogicalType
  notNull: boolean
  blank: boolean
  desc: string
  defaultValue?: string
  checkExpression?: string
}

export interface MetadataIndex {
  id: string
  name: string
  cols: string[]
  unique: boolean
  type: string
}

export interface MetadataTable {
  id: string
  schema: string
  name: string
  fullName: string
  comment: string
  g: string
  width?: number
  cols: MetadataColumn[]
  indexes: MetadataIndex[]
  /** Legado: posicoes coexistem com layout.positions. Adapter prioriza layout. */
  x?: number
  y?: number
}

export interface MetadataWaypoint {
  x: number
  y: number
}

export interface MetadataRelationship {
  id: string
  from: string
  to: string
  col: string
  type: RelationType
  fromCardinality?: 'one' | 'many' | 'zero-one' | 'zero-many'
  toCardinality?: 'one' | 'many' | 'zero-one' | 'zero-many'
  mid: number | null
  fromAnchor: MetadataAnchor | null
  toAnchor: MetadataAnchor | null
  waypoints: MetadataWaypoint[]
  isManual?: boolean
  labelT?: number
  constraintName?: string
  relationshipKey?: string
}

export interface MetadataGroup {
  id: string
  name: string
  color: string
}

export interface MetadataPosition {
  x: number
  y: number
}

export interface MetadataLayout {
  positions: Record<string, MetadataPosition>
  zoom: number
  pan: { x: number; y: number }
}

export interface MetadataProject {
  name: string
  description: string
  dialect: DatabaseDialect
}

export interface MetadataDefaults {
  prefix: MetadataColumn[]
  suffix: MetadataColumn[]
}

export interface MetadataUserView {
  hiddenTableIds: string[]
  sidebarCollapsed: boolean
  theme: 'system' | 'light' | 'dark'
}

export interface MetadataSettings {
  defaults: MetadataDefaults
  defaultLineMode: 'orthogonal' | 'straight' | 'bezier'
  relAnimation: boolean
  grid: boolean
  /** Per-user; presente em draft, ausente em commit. */
  userView?: MetadataUserView
}

export interface ProjectMetadataV1 {
  schemaVersion: SchemaVersion
  project: MetadataProject
  tables: MetadataTable[]
  relationships: MetadataRelationship[]
  groups: MetadataGroup[]
  layout: MetadataLayout
  settings: MetadataSettings
}

export function emptyMetadataV1(
  project: MetadataProject = { name: '', description: '', dialect: 'postgresql' },
): ProjectMetadataV1 {
  return {
    schemaVersion: METADATA_SCHEMA_VERSION,
    project,
    tables: [],
    relationships: [],
    groups: [],
    layout: { positions: {}, zoom: 1, pan: { x: 0, y: 0 } },
    settings: {
      defaults: { prefix: [], suffix: [] },
      defaultLineMode: 'orthogonal',
      relAnimation: true,
      grid: true,
    },
  }
}
